import { el } from "./dom";
import type { PhotoEntry } from "../content/photos.generated";

const FORMATS = [
  { type: "image/avif", ext: "avif" },
  { type: "image/webp", ext: "webp" },
  { type: "image/jpeg", ext: "jpg" },
] as const;

export interface PictureOpts {
  entry: PhotoEntry;
  alt: string;
  sizes?: string;
  loading?: "lazy" | "eager";
  fetchpriority?: "high" | "low" | "auto";
  className?: string;
}

/**
 * Build a <picture> element with AVIF/WebP/JPG sources + LQIP blur background.
 * The LQIP shows immediately on a tiny base64 jpeg; the real image fades in
 * once decoded.
 */
export function picture(opts: PictureOpts): HTMLPictureElement {
  const { entry, alt, sizes = "100vw", loading = "lazy", fetchpriority, className } = opts;
  const pic = document.createElement("picture");
  if (className) pic.className = className;

  for (const fmt of FORMATS) {
    const src = document.createElement("source");
    src.type = fmt.type;
    src.srcset = entry.widths.map((w) => `/img/${entry.folder}/${entry.base}-${w}.${fmt.ext} ${w}w`).join(", ");
    src.sizes = sizes;
    pic.appendChild(src);
  }

  const fallbackWidth = entry.widths[entry.widths.length - 1] ?? 1280;
  const img = el("img", {
    src: `/img/${entry.folder}/${entry.base}-${fallbackWidth}.jpg`,
    alt,
    loading,
    decoding: "async",
    width: fallbackWidth,
    height: Math.round(fallbackWidth / entry.aspect),
  }) as HTMLImageElement;
  if (fetchpriority) img.setAttribute("fetchpriority", fetchpriority);

  // LQIP background while loading
  img.style.background = `center / cover no-repeat url('${entry.lqip}')`;
  img.addEventListener("load", () => {
    img.style.background = "";
  }, { once: true });

  pic.appendChild(img);
  return pic;
}

/** URL of the largest variant — useful for WebGL textures. */
export function heroUrl(entry: PhotoEntry): string {
  const w = entry.widths[entry.widths.length - 1] ?? 1280;
  return `/img/${entry.folder}/${entry.base}-${w}.jpg`;
}
